import React, { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { X, FloppyDisk, Paperclip } from '@phosphor-icons/react';
import RichEditor, { RichEditorContent } from './RichEditor';

// Detect whether a stored value is HTML (new, TipTap output) or legacy markdown.
// Legacy values have no HTML tags; TipTap always wraps content in at least a <p>.
export function isHtml(v) {
  return typeof v === 'string' && /<\w+[^>]*>/.test(v);
}

// Inline editable field with save actions + image paste + file upload.
// Single-click to edit (Jira/Asana-style). Esc cancels, Enter / blur saves for simple inputs.
export function InlineField({ value, displayValue, type = 'text', options, onSave, canEdit, richText, onPaste, onFileUpload, uploadImage, maxLength }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);

  const save = () => { if (val !== value) onSave(val); setEditing(false); };
  const cancel = () => { setVal(value); setEditing(false); };

  if (!canEdit) {
    // Render HTML via RichEditorContent when the stored value looks like HTML, else fall back to caller-provided display.
    if (richText && isHtml(value)) return <RichEditorContent html={value} />;
    return <div className="text-[13px] text-zinc-700">{displayValue || value || '-'}</div>;
  }

  if (editing) {
    if (options) {
      return (
        <Select value={val || ''} defaultOpen onValueChange={(v) => { onSave(v); setEditing(false); }} onOpenChange={(open) => { if (!open) setEditing(false); }}>
          <SelectTrigger className="h-8 text-[12px] border-blue-400 ring-2 ring-blue-100"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[320px]">{options.map(o => <SelectItem key={o.value} value={o.value} className="text-[13px] py-1.5 cursor-pointer">{o.node || o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }

    if (richText) {
      return (
        <div className="space-y-2">
          <RichEditor
            value={val}
            onChange={setVal}
            uploadImage={uploadImage}
            autoFocus
            placeholder="Write content… Paste screenshots directly."
            minHeight={140}
          />
          <div className="flex items-center justify-end gap-1.5">
            <Button variant="ghost" size="sm" onClick={cancel} className="h-7 px-2 text-[11px] text-zinc-500 hover:text-zinc-700">
              <X size={12} className="mr-0.5" /> Cancel
            </Button>
            <Button size="sm" onClick={save} className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white text-[11px]">
              <FloppyDisk size={12} className="mr-1" /> Save
            </Button>
          </div>
        </div>
      );
    }

    if (type === 'textarea') {
      return (
        <div className="space-y-2">
          <Textarea
            value={val}
            onChange={e => setVal(e.target.value)}
            onPaste={onPaste ? (e) => onPaste(e, val, setVal) : undefined}
            onKeyDown={e => { if (e.key === 'Escape') cancel(); }}
            autoFocus
            rows={4}
            className="text-[13px] border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {onFileUpload && (
                <button type="button" onClick={() => onFileUpload(setVal)} className="flex items-center gap-1 text-[10px] text-zinc-400 hover:text-blue-600 transition-colors" title="Attach image">
                  <Paperclip size={12} /> Attach image
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={cancel} className="h-7 px-2 text-[11px] text-zinc-500 hover:text-zinc-700">
                <X size={12} className="mr-0.5" /> Cancel
              </Button>
              <Button size="sm" onClick={save} className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white text-[11px]">
                <FloppyDisk size={12} className="mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Simple input (text/number/date/url): save on blur or Enter, cancel on Escape — feels like Jira/Asana.
    // For date fields we block past dates at the browser level.
    const extraProps = type === 'date' ? { min: new Date().toISOString().split('T')[0] } : {};
    return (
      <Input type={type} value={val || ''} onChange={e => setVal(e.target.value)}
        {...extraProps}
        maxLength={maxLength}
        placeholder={type === 'url' ? 'https://…' : undefined}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') cancel(); }}
        onBlur={save}
        autoFocus className="h-8 text-[13px] border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100" />
    );
  }

  // Display mode — render HTML via RichEditorContent for richText fields with HTML values.
  const renderedDisplay = richText && isHtml(value)
    ? <RichEditorContent html={value} />
    : (displayValue || value || <span className="text-zinc-400 italic">Click to set</span>);
  return (
    <div onClick={() => setEditing(true)}
      className="text-[13px] text-zinc-700 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-zinc-100/80 hover:ring-1 hover:ring-zinc-200 transition-all min-h-[28px] flex flex-col"
      title="Click to edit">
      {renderedDisplay}
    </div>
  );
}

// Render rich content with inline images
export function RichContent({ content }) {
  if (!content) return <span className="text-zinc-400 italic">Click to add</span>;
  const parts = content.split(/(!\[.*?\]\(.*?\))/g);
  return (
    <div className="text-[13px] text-zinc-600 leading-relaxed whitespace-pre-wrap">
      {parts.map((part, i) => {
        const imgMatch = part.match(/!\[(.*?)\]\((.*?)\)/);
        if (imgMatch) return <img key={i} src={imgMatch[2]} alt={imgMatch[1]} className="max-w-full rounded-md border border-zinc-200 my-2" />;
        return <span key={i}>{part}</span>;
      })}
    </div>
  );
}

// Default layout. `useSprints` is true when the project uses sprints (scrum template OR has ≥1 sprint OR the task is already assigned to one).
export function getEffectiveLayout(fieldLayouts, workType, customFields, project, useSprints) {
  const layout = fieldLayouts?.[workType];
  const defaultMain = ['description', 'subtasks', 'linked_issues'];
  const showSprint = !!useSprints || project?.template === 'scrum';
  const defaultSidebar = ['type', 'status', 'priority', 'assignee', ...(showSprint ? ['sprint'] : []), 'due_date', 'tags', 'reporter', 'created'];
  const cfIds = (customFields || [])
    .filter(cf => !cf.work_types?.length || cf.work_types.includes(workType))
    .map(cf => cf.field_id);
  const defSb = [...defaultSidebar, ...cfIds];
  if (!layout) return { main: defaultMain, sidebar: defSb };
  const placed = [...(layout.main || []), ...(layout.sidebar || [])];
  // Only auto-surface newly-created CUSTOM fields. Never re-add builtin fields
  // the PM has explicitly removed from a saved layout (e.g. Sub-tasks / Linked
  // Items) — otherwise removal would be impossible. Projects with no saved
  // layout fall through to the full defaults above.
  const missingCustom = cfIds.filter(id => !placed.includes(id));
  return {
    main: layout.main || defaultMain,
    sidebar: [...(layout.sidebar || defaultSidebar), ...missingCustom],
  };
}

export const LABEL_CLS = "text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold mb-1.5 block";
